// package imports
import { useTheme } from '@mui/material/styles';
import { Typography } from '@mui/material';

// local imports
import Section from 'components/common/subcomponents/Section';
import {
  AgilityContentProperties,
  ContentStyle,
  HeaderLevel,
} from 'types/AgilityTypes';
import { getContentStyle } from 'services/styleUtils/contentStyleUtils';
import { useReveal } from 'hooks/useReveal';

type TimelineItem = {
  contentID: number;
  properties: AgilityContentProperties;
  fields: {
    title: string;
    description: string;
  };
};

type SectionStyleType = {
  contentID: number;
  properties: AgilityContentProperties;
  fields: {
    name: string;
    margin: string;
  };
};

interface TimelineFields {
  items: TimelineItem[];
  sectionStyle: SectionStyleType;
  sectionId: string;
  sectionTitle: string;
  titleLevel: HeaderLevel;
  sectionTitleStyle: ContentStyle;
  sectionSubtitle: string;
  subtitleStyle: ContentStyle;
}

interface TimelineProps {
  module: {
    fields: TimelineFields;
  };
}

const TRANSITION_DELAY_STEP = 0.15; // seconds

const Timeline: React.FC<TimelineProps> = ({ module: { fields } }) => {
  const theme = useTheme();
  const wrapRef = useReveal();

  const titleTag =
    typeof fields.titleLevel === 'number'
      ? (('h' + fields.titleLevel) as 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6')
      : fields.titleLevel || 'h1';

  const titleStyle = getContentStyle(theme, fields.sectionTitleStyle.fields);
  const subtitleStyle = getContentStyle(theme, fields.subtitleStyle.fields);

  return (
    <div ref={wrapRef}>
      <Section id={fields.sectionId} styleProps={fields.sectionStyle}>
        <Typography
          component={titleTag}
          className="rv font-serif"
          style={{
            color: titleStyle.textColor,
            textAlign: titleStyle.alignment,
            fontSize: titleStyle.fontSize,
            fontWeight: titleStyle.fontWeight,
            lineHeight: titleStyle.lineHeight,
            marginBottom: '2rem',
          }}
        >
          {fields.sectionTitle}
        </Typography>
        <div
          className="rv"
          style={{
            color: subtitleStyle.textColor,
            textAlign: subtitleStyle.alignment,
            fontSize: subtitleStyle.fontSize,
            fontWeight: subtitleStyle.fontWeight,
            lineHeight: subtitleStyle.lineHeight,
            marginBottom: '3rem',
          }}
        >
          {fields.sectionSubtitle}
        </div>
        <div className="space-y-12 md:space-y-0 mb-16">
          {fields.items.map((item, i, items) => (
            <div
              key={item.contentID}
              className={`rv md:flex items-center gap-10 ${i % 2 === 1 ? 'md:flex-row-reverse' : ''} ${i > 0 ? 'md:mt-[-1rem]' : ''}`}
              data-anim={i % 2 === 0 ? 'fade-right' : 'fade-left'}
              style={{ transitionDelay: `${i * TRANSITION_DELAY_STEP}s` }}
            >
              <div className="flex-1 mb-6 md:mb-0">
                <div
                  className="p-7 rounded-3xl relative overflow-hidden"
                  style={{
                    backgroundColor: '#ffffff',
                    border: '1px solid rgba(0,0,0,0.08)',
                    boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
                  }}
                >
                  <div className="relative z-10">
                    <h3
                      className="text-lg font-bold mb-2"
                      style={{ color: '#0f172a' }}
                    >
                      {item.fields.title}
                    </h3>
                    <p
                      className="text-sm leading-relaxed"
                      style={{ color: '#475569' }}
                    >
                      {item.fields.description}
                    </p>
                  </div>
                </div>
              </div>
              <div className="hidden md:flex flex-col items-center gap-0 shrink-0">
                <div
                  className="w-6 h-6 rounded-full border-[3px]"
                  style={{
                    borderColor: '#4F99BD',
                    backgroundColor: '#ffffff',
                    boxShadow: `0 0 0 5px rgba(59,130,246,0.2), 0 0 16px rgba(59,130,246,0.15)`,
                  }}
                />
                {i < items.length - 1 && (
                  <div
                    className="w-[3px] h-20 rounded-full"
                    style={{
                      background: `linear-gradient(to bottom, #4F99BD, rgba(59,130,246,0.15))`,
                    }}
                  />
                )}
              </div>
              <div className="flex-1" />
            </div>
          ))}
        </div>
      </Section>
    </div>
  );
};

export default Timeline;
